import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Store, Plus, Edit, MapPin, Phone, FileText, Image, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const StoreManagement = () => {
  const [stores, setStores] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    gst_number: '',
    fssai_number: '',
    email: '',
    show_images_in_pos: true
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const response = await apiClient.get('/stores');
      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
      toast.error('Failed to load stores');
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      code: '',
      address: '',
      phone: '',
      gst_number: '',
      fssai_number: '',
      email: '',
      show_images_in_pos: true
    });
    setShowCreateModal(true);
  };

  const handleEdit = (store) => {
    setSelectedStore(store);
    setFormData({
      name: store.name,
      code: store.code,
      address: store.address,
      phone: store.phone || '',
      gst_number: store.gst_number || '',
      fssai_number: store.fssai_number || '',
      email: store.email || '',
      show_images_in_pos: store.config?.show_images_in_pos !== false
    });
    setShowEditModal(true);
  };

  const handleSaveCreate = async () => {
    if (!formData.name || !formData.code || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await apiClient.post('/stores', formData);
      toast.success('Store created successfully!');
      setShowCreateModal(false);
      loadStores();
    } catch (error) {
      console.error('Error creating store:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create store';
      toast.error(errorMessage);
    }
  };

  const handleSaveEdit = async () => {
    if (!formData.name || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await apiClient.put(`/stores/${selectedStore.id}`, formData);
      toast.success('Store updated successfully!');
      setShowEditModal(false);
      loadStores();
    } catch (error) {
      console.error('Error updating store:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update store';
      toast.error(errorMessage);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold">Store Management</h2>
          <p className="text-gray-600 mt-1">Manage all SSPICE store locations</p>
        </div>
        <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Add New Store
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stores.map((store) => (
          <Card key={store.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl flex items-center justify-between">
                <div className="flex items-center">
                  <Store className="w-6 h-6 mr-2 text-orange-600" />
                  {store.name}
                </div>
                <Button size="sm" variant="outline" onClick={() => handleEdit(store)} data-testid="edit-store-btn">
                  <Edit className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-1 text-gray-500 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{store.address}</p>
                </div>
                {store.phone && (
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-500" />
                    <p className="text-sm text-gray-700">{store.phone}</p>
                  </div>
                )}
                {store.gst_number && (
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-gray-500" />
                    <p className="text-sm text-gray-700">GST: {store.gst_number}</p>
                  </div>
                )}
                {store.fssai_number && (
                  <div className="flex items-center">
                    <ShieldCheck className="w-4 h-4 mr-2 text-gray-500" />
                    <p className="text-sm text-gray-700">FSSAI: {store.fssai_number}</p>
                  </div>
                )}
                <div className="pt-2 border-t">
                  <span className="text-xs text-gray-500">Store Code: </span>
                  <span className="text-xs font-mono font-bold bg-gray-100 px-2 py-1 rounded">{store.code}</span>
                </div>
                <div className="pt-2 flex items-center">
                  <Image className="w-3.5 h-3.5 mr-2 text-gray-400" />
                  <span className="text-xs text-gray-600">
                    POS Images: {store.config?.show_images_in_pos !== false ? 
                      <span className="text-green-600 font-semibold">ON</span> : 
                      <span className="text-orange-600 font-semibold">OFF</span>}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Store Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Store</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Store Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="SSPICE Pizza - Badlapur"
              />
            </div>
            <div>
              <Label>Store Code * (unique)</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="BADLAPUR"
              />
            </div>
            <div className="col-span-2">
              <Label>Address *</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Shop No., Street, Landmark, City, Pincode"
                rows={3}
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="9156986500"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="store@sspicepizza.com"
              />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input
                value={formData.gst_number}
                onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
                placeholder="27AIJPB0377J1Z9"
              />
            </div>
            <div>
              <Label>FSSAI Number</Label>
              <Input
                value={formData.fssai_number}
                onChange={(e) => setFormData({ ...formData, fssai_number: e.target.value })}
                placeholder="10012345678901234"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCreate} className="bg-green-600 hover:bg-green-700">
              Create Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Store Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Store</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Store Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Store Code</Label>
              <Input
                value={formData.code}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Code cannot be changed</p>
            </div>
            <div className="col-span-2">
              <Label>Address *</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input
                value={formData.gst_number}
                onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label>FSSAI Number</Label>
              <Input
                value={formData.fssai_number}
                onChange={(e) => setFormData({ ...formData, fssai_number: e.target.value })}
                placeholder="10012345678901234"
              />
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                <div className="flex items-start space-x-3">
                  <Image className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <Label className="text-base font-semibold">Show Images in POS</Label>
                    <p className="text-sm text-gray-600 mt-1">
                      When enabled, menu items will display images in POS. Disable for faster performance.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.show_images_in_pos}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_images_in_pos: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreManagement;
